import type { AnalyzeTicketResult, CaseDetailResult, PremiumProductKey } from "@/lib/api";
import { resolveCaseEntitlements } from "@/lib/entitlements";

type AnalyticsPayload = Record<string, unknown>;
type DataLayerPrimitive = string | number | boolean;
type DataLayerPayload = Record<string, DataLayerPrimitive | Array<Record<string, DataLayerPrimitive>>>;

type GtagFn = (...args: unknown[]) => void;
type UetQueue = Array<unknown> | { push: (...args: unknown[]) => unknown };

declare global {
  interface Window {
    dataLayer?: Array<Record<string, DataLayerPrimitive>>;
    gtag?: GtagFn;
    uetq?: UetQueue;
    JUSTICEDRIVE_ANALYTICS?: {
      gaMeasurementId?: string;
      googleAdsId?: string;
      googleAdsConversionLabel?: string;
      microsoftUetTagId?: string;
    };
    __JUSTICEDRIVE_GET_ANALYTICS_PAGE__?: () => { path: string; location: string };
    loadJusticeDriveMarketingTags?: (preferences: { analytics: boolean; advertising: boolean }) => void;
  }
}

const defaultGaMeasurementId = "G-B046PR81DK";
const defaultGoogleAdsId = "AW-18363183270";
const defaultGoogleAdsConversionLabel = "oprmCIvA-NkcEKbhn7RE";
const defaultMicrosoftUetTagId = "211075727";
const purchaseConversionStoragePrefix = "justicedrive_purchase_conversion:";
const pendingMicrosoftUetEventsStorageKey = "justicedrive_pending_uet_events";
const purchaseConversionMemory = new Set<string>();

const microsoftUetEvents = new Set([
  "intake_viewed",
  "upload_cta_clicked",
  "upload_started",
  "upload_completed",
  "ocr_review_started",
  "ocr_review_completed",
  "ocr_completed",
  "ocr_failed",
  "narrative_started",
  "intake_abandoned",
  "intake_resumed",
  "analyze_started",
  "result_viewed",
  "checkout_started",
  "purchase",
  "recovery_link_opened",
  "recovery_resumed",
  "recovery_checkout_started",
  "recovery_purchase"
]);

const microsoftRevenueEvents = new Set(["purchase"]);
const blockedClientEvents = new Set(["analyze_completed", "purchase_started", "payment_completed", "payment_confirmed"]);

const getGaMeasurementId = (): string =>
  String(
    import.meta.env.VITE_GA_MEASUREMENT_ID ??
      (typeof window !== "undefined" ? window.JUSTICEDRIVE_ANALYTICS?.gaMeasurementId : undefined) ??
      defaultGaMeasurementId
  ).trim();

const getGoogleAdsId = (): string =>
  String(
    import.meta.env.VITE_GOOGLE_ADS_ID ??
      (typeof window !== "undefined" ? window.JUSTICEDRIVE_ANALYTICS?.googleAdsId : undefined) ??
      defaultGoogleAdsId
  ).trim();

const getGoogleAdsConversionLabel = (): string =>
  String(
    import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL ??
      (typeof window !== "undefined" ? window.JUSTICEDRIVE_ANALYTICS?.googleAdsConversionLabel : undefined) ??
      defaultGoogleAdsConversionLabel
  ).trim();

const getMicrosoftUetTagId = (): string =>
  String(
    import.meta.env.VITE_MICROSOFT_UET_TAG_ID ??
      (typeof window !== "undefined" ? window.JUSTICEDRIVE_ANALYTICS?.microsoftUetTagId : undefined) ??
      defaultMicrosoftUetTagId
  ).trim();

const dataLayerParamAllowlist = new Set([
  "source_page",
  "file_type",
  "stage",
  "resumed",
  "time_bucket",
  "reason",
  "status",
  "missing_violation_code",
  "court_address_verified",
  "geo_map_safe",
  "records_recommended_count",
  "mail_lifecycle_state",
  "product_access_state",
  "product_selected",
  "transaction_id",
  "value",
  "currency",
  "calculator_type",
  "result_band",
  "guide_slug",
  "cta_location",
  "product",
  "base_price",
  "offer_type",
  "offer_price",
  "entry_point",
  "attempt_number",
  "revenue_recovery_variant",
  "close_reason",
  "recovery_step",
  "abandonment_type",
  "recovery_channel",
  "landing_type"
  ,"items"
]);

export const PRODUCT_VALUES: Record<PremiumProductKey, number> = {
  court_pdf: 39,
  mail_filing: 79,
  premium_defense: 119,
  records_request: 49
};

const hasGtag = (): boolean => typeof window !== "undefined" && typeof window.gtag === "function";

const normalizeDataLayerString = (value: unknown): string => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return normalized.slice(0, 64) || "unknown";
};

const safePagePath = (path: string): string => {
  const [pathname = "/", query = ""] = path.split("?");
  const normalizedPath = pathname
    .replace(/^\/analysis\/[^/?#]+/, "/analysis/:case_id")
    .replace(/^\/defense-hub\/[^/?#]+/, "/defense-hub/:case_id")
    .replace(/^\/records\/[^/?#]+/, "/records/:case_id")
    .replace(/^\/mail\/[^/?#]+/, "/mail/:case_id")
    .replace(/^\/admin\/cases\/[^/?#]+/, "/admin/cases/:case_id")
    .replace(/^\/admin\/trace\/[^/?#]+/, "/admin/trace/:case_id");
  if (normalizedPath === "/checkout/cancel" || normalizedPath === "/checkout/success" || normalizedPath === "/checkout/recover") {
    return normalizedPath;
  }
  if (normalizedPath !== pathname && query) {
    const params = new URLSearchParams(query);
    for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "gbraid", "wbraid", "msclkid"]) {
      params.delete(key);
    }
    const safeQuery = params.toString();
    return safeQuery ? `${normalizedPath}?${safeQuery}` : normalizedPath;
  }
  return query ? `${normalizedPath}?${query}` : normalizedPath;
};

const buildSafePageLocation = (safePath: string): string => {
  if (typeof window === "undefined") {
    return safePath;
  }
  try {
    return new URL(safePath, window.location.origin).toString();
  } catch {
    return safePath;
  }
};

const getSafeCurrentPage = (): { path: string; location: string; sensitive: boolean } => {
  if (typeof window === "undefined") {
    return { path: "/", location: "/", sensitive: false };
  }
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const path = safePagePath(currentPath);
  return {
    path,
    location: buildSafePageLocation(path),
    sensitive: path !== currentPath,
  };
};

const buildGoogleEventParams = (params: DataLayerPayload): Record<string, DataLayerPrimitive> => {
  const page = getSafeCurrentPage();
  if (!page.sensitive) {
    return params;
  }
  return {
    ...params,
    page_path: page.path,
    page_location: page.location,
    page_referrer: `${window.location.origin}/`,
    send_to: getGaMeasurementId(),
  };
};

const sanitizeDataLayerParams = (params: AnalyticsPayload, eventName?: string): DataLayerPayload => {
  const sanitized: DataLayerPayload = {};
  for (const [key, value] of Object.entries(params)) {
    if (!dataLayerParamAllowlist.has(key) && !(key === "violation_code" && eventName?.startsWith("cvc_"))) {
      continue;
    }
    if (key === "items" && Array.isArray(value)) {
      const items = value
        .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object")
        .map((item) => ({
          item_id: normalizeDataLayerString(item.item_id),
          item_name: normalizeDataLayerString(item.item_name),
          price: typeof item.price === "number" && Number.isFinite(item.price) ? item.price : 0,
          quantity: typeof item.quantity === "number" && Number.isFinite(item.quantity) ? item.quantity : 1,
        }))
        .filter((item) => item.item_id !== "unknown" && item.item_name !== "unknown" && item.price > 0);
      if (items.length) sanitized.items = items;
    } else if (typeof value === "boolean" || typeof value === "number") {
      sanitized[key] = value;
    } else if (typeof value === "string") {
      sanitized[key] = key === "currency" ? value.trim().toUpperCase().slice(0, 3) : normalizeDataLayerString(value);
    }
  }
  return sanitized;
};

const buildMicrosoftUetParams = (event: string, params: DataLayerPayload): DataLayerPayload => {
  const uetParams: DataLayerPayload = {};
  if (typeof params.source_page === "string") {
    uetParams.source_page = params.source_page;
  }
  if (typeof params.status === "string") {
    uetParams.status = params.status;
  }
  if (typeof params.product_selected === "string") {
    uetParams.product_selected = params.product_selected;
  }
  if (event === "purchase" && typeof params.transaction_id === "string") {
    uetParams.transaction_id = params.transaction_id;
  }
  if (microsoftRevenueEvents.has(event)) {
    if (typeof params.value === "number") {
      uetParams.revenue_value = params.value;
    }
    if (typeof params.currency === "string") {
      uetParams.currency = params.currency;
    }
  }
  return uetParams;
};

const trackMicrosoftUetEvent = (event: string, params: DataLayerPayload): void => {
  if (typeof window === "undefined" || !getMicrosoftUetTagId() || !microsoftUetEvents.has(event)) {
    return;
  }
  try {
    const page = getSafeCurrentPage();
    const canSendSanitizedSuccessPurchase = page.sensitive && page.path === "/checkout/success" && event === "purchase";
    if (page.sensitive && !canSendSanitizedSuccessPurchase) {
      const pending = JSON.parse(window.sessionStorage.getItem(pendingMicrosoftUetEventsStorageKey) ?? "[]") as Array<{
        event: string;
        params: DataLayerPayload;
      }>;
      pending.push({ event, params: buildMicrosoftUetParams(event, params) });
      window.sessionStorage.setItem(pendingMicrosoftUetEventsStorageKey, JSON.stringify(pending.slice(-20)));
      return;
    }
    window.uetq = window.uetq ?? [];
    window.uetq.push("event", event, buildMicrosoftUetParams(event, params));
  } catch {
    // Never block user flow on analytics.
  }
};

const trackMicrosoftUetPageView = (safePath: string): void => {
  if (typeof window === "undefined" || !getMicrosoftUetTagId()) {
    return;
  }
  try {
    window.uetq = window.uetq ?? [];
    window.uetq.push("event", "page_view", {
      page_path: safePath,
      page_title: document.title,
    });
    if (!getSafeCurrentPage().sensitive) {
      const pending = JSON.parse(window.sessionStorage.getItem(pendingMicrosoftUetEventsStorageKey) ?? "[]") as Array<{
        event: string;
        params: DataLayerPayload;
      }>;
      for (const queued of pending) {
        if (microsoftUetEvents.has(queued.event)) {
          window.uetq.push("event", queued.event, queued.params);
        }
      }
      window.sessionStorage.removeItem(pendingMicrosoftUetEventsStorageKey);
    }
  } catch {
    // Never block user flow on analytics.
  }
};

export const track = (event: string, payload?: AnalyticsPayload): void => {
  if (!hasGtag()) {
    return;
  }
  try {
    window.gtag?.("event", normalizeDataLayerString(event), buildGoogleEventParams(sanitizeDataLayerParams(payload ?? {})));
  } catch {
    // Never block user flow on analytics.
  }
};

export const trackEvent = (eventName: string, params: AnalyticsPayload = {}): void => {
  if (typeof window === "undefined") {
    return;
  }
  const event = normalizeDataLayerString(eventName);
  if (!event || blockedClientEvents.has(event)) {
    return;
  }
  try {
    window.dataLayer = window.dataLayer ?? [];
    const safeParams = sanitizeDataLayerParams(params, event);
    window.dataLayer.push({ event, ...safeParams });
    if (hasGtag()) {
      window.gtag?.("event", event, buildGoogleEventParams(safeParams));
    }
    trackMicrosoftUetEvent(event, safeParams);
  } catch {
    // Never block user flow on analytics.
  }
};

const actionDedupePrefix = "justicedrive_analytics_action:";

/** Dedupe one logical action, while allowing a different action key to fire. */
export const trackEventOnce = (eventName: string, actionKey: string, params: AnalyticsPayload = {}): boolean => {
  if (typeof window === "undefined") return false;
  const key = `${actionDedupePrefix}${normalizeDataLayerString(eventName)}:${normalizeDataLayerString(actionKey)}`;
  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, "1");
  } catch {
    // Continue without persistence when storage is unavailable.
  }
  trackEvent(eventName, params);
  return true;
};

export const buildCheckoutEventParams = (productKey: PremiumProductKey | null | undefined): DataLayerPayload => {
  const product = productKey && productKey in PRODUCT_VALUES ? productKey : "premium_defense";
  return {
    product_selected: product,
    value: PRODUCT_VALUES[product],
    currency: "USD"
  };
};

const buildGoogleAdsConversionSendTo = (): string => {
  const googleAdsId = getGoogleAdsId();
  const conversionLabel = getGoogleAdsConversionLabel();
  return googleAdsId && conversionLabel ? `${googleAdsId}/${conversionLabel}` : "";
};

const PRODUCT_NAMES: Record<PremiumProductKey, string> = {
  court_pdf: "Court PDF",
  records_request: "Records Request Pack",
  mail_filing: "Certified Mail Filing",
  premium_defense: "Premium Defense",
};

const hasTrackedPurchaseConversion = (storageKey: string): boolean => {
  if (purchaseConversionMemory.has(storageKey)) {
    return true;
  }
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(storageKey) !== null;
  } catch {
    return false;
  }
};

const markPurchaseConversionTracked = (storageKey: string, payload: DataLayerPayload): void => {
  purchaseConversionMemory.add(storageKey);
  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        ...payload,
        tracked_at: new Date().toISOString()
      })
    );
  } catch {
    // In-memory dedupe still protects the current page lifecycle.
  }
};

export const trackPurchaseConversionOnce = (
  transactionId: string | null | undefined,
  amountTotalCents: number | null | undefined,
  currency: string | null | undefined,
  productSelected: PremiumProductKey | null | undefined,
  sourcePage: string
): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  const cleanTransactionId = String(transactionId ?? "").trim();
  if (!cleanTransactionId || typeof amountTotalCents !== "number" || !Number.isFinite(amountTotalCents) || amountTotalCents <= 0) {
    return false;
  }
  const storageKey = `${purchaseConversionStoragePrefix}${cleanTransactionId}`;
  if (hasTrackedPurchaseConversion(storageKey)) {
    return false;
  }

  const value = Math.round((amountTotalCents / 100) * 100) / 100;
  const normalizedCurrency = String(currency ?? "USD").trim().toUpperCase().slice(0, 3) || "USD";
  if (normalizedCurrency !== "USD") {
    return false;
  }
  const product = productSelected && productSelected in PRODUCT_VALUES ? productSelected : "premium_defense";
  const items = [{
    item_id: product,
    item_name: PRODUCT_NAMES[product],
    price: value,
    quantity: 1,
  }];
  const purchaseParams: DataLayerPayload = {
    product_selected: product,
    source_page: sourcePage,
    transaction_id: cleanTransactionId,
    value,
    currency: normalizedCurrency,
    items,
  };

  trackEvent("purchase", purchaseParams);

  const sendTo = buildGoogleAdsConversionSendTo();
  if (hasGtag() && sendTo) {
    try {
      window.gtag?.("event", "conversion", {
        send_to: sendTo,
        value,
        currency: normalizedCurrency,
        transaction_id: cleanTransactionId
      });
    } catch {
      // Never block user flow on conversion tracking.
    }
  }

  markPurchaseConversionTracked(storageKey, purchaseParams);
  return true;
};

export const getProductAccessState = (caseDetail: CaseDetailResult | null | undefined): "free" | "paid" | "premium" => {
  const entitlements = resolveCaseEntitlements(caseDetail);
  if (entitlements.premium_defense) {
    return "premium";
  }
  if (entitlements.court_pdf || entitlements.mail_filing || entitlements.records_request) {
    return "paid";
  }
  return "free";
};

const hasTextValue = (value: unknown): boolean => typeof value === "string" && value.trim().length > 0;

const hasVerifiedLedgerField = (analysis: AnalyzeTicketResult | null | undefined, field: string): boolean =>
  Boolean(analysis?.evidence_ledger?.some((entry) => entry.field === field && entry.status === "verified" && !entry.requires_verification));

export const buildAnalyzeCompletedEventParams = (
  analysis: AnalyzeTicketResult | null | undefined,
  options: {
    mailLifecycleState?: string | null;
    productAccessState?: "free" | "paid" | "premium";
  } = {}
): DataLayerPayload => {
  const violationCode =
    analysis?.citation_facts?.violation_code?.value ??
    analysis?.evidence_ledger?.find((entry) => entry.field === "violation_code")?.value;
  const plan = analysis?.records_request_plan;
  return {
    missing_violation_code: !hasTextValue(violationCode),
    court_address_verified: hasVerifiedLedgerField(analysis, "court_address"),
    geo_map_safe: analysis?.geo?.map_safe === true && analysis.geo.validation_status === "accepted",
    records_recommended_count: Array.isArray(plan?.items) ? plan.items.length : Number(plan?.item_count ?? 0),
    mail_lifecycle_state: options.mailLifecycleState ?? "unknown",
    product_access_state: options.productAccessState ?? "free"
  };
};

export const trackPageView = (path: string): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const safePath = safePagePath(path);
    const safeLocation = buildSafePageLocation(safePath);
    trackMicrosoftUetPageView(safePath);
    const pageViewParams: Record<string, DataLayerPrimitive> = {
      source_page: normalizeDataLayerString(safePath),
      page_path: safePath,
      page_location: safeLocation,
      page_referrer: safePath !== path ? `${window.location.origin}/` : document.referrer,
      send_to: getGaMeasurementId(),
    };
    window.gtag?.("set", {
      page_path: safePath,
      page_location: safeLocation,
      page_referrer: pageViewParams.page_referrer,
    });
    window.gtag?.("event", "page_view", pageViewParams);
  } catch {
    // Never block user flow on analytics.
  }
};

export const pageView = trackPageView;
